import { Link } from 'react-router';
import { MCP_TOOLS } from '@infras/shared-types';

/**
 * The 3-step "connect your AI client" walkthrough — shared by the MCP docs page
 * and the Get Started (new-member onboarding) page so the instructions can't
 * drift. Step 1 links to Settings → Access unless the token panel is rendered
 * inline next to this component (Get Started does that).
 */
const ENTRYPOINT = 'https://infras-mcp.alphasphere.io/mcp';
const HEALTH_URL = 'https://infras-mcp.alphasphere.io/health';

const CONNECT_HTTP = `{
  "mcpServers": {
    "optisigns-infras": {
      "type": "http",
      "url": "${ENTRYPOINT}",
      "headers": { "Authorization": "Bearer <YOUR_TOKEN>" }
    }
  }
}`;

const HEALTH_CHECK = `curl ${HEALTH_URL}
# → { "ok": true, "tools": ${MCP_TOOLS.length}, "authRequired": true }`;

export function McpConnectSteps({ tokenPanelInline = false }: { tokenPanelInline?: boolean }) {
  return (
    <>
      <ol className="docs-prose">
        <li>
          <strong>Generate a personal token.</strong>{' '}
          {tokenPanelInline ? (
            <>
              Use the token panel above — click <em>Create token</em> (give it a label like{' '}
              <code>claude desktop</code>) and copy the <code>pat_…</code> value.
            </>
          ) : (
            <>
              Open <Link to="/settings/access">Settings → Access → Personal access tokens</Link>,
              click <em>Create token</em> (give it a label like <code>claude desktop</code>), and
              copy the <code>pat_…</code> value.
            </>
          )}{' '}
          It's shown <strong>once</strong> — if you lose it, revoke it and mint a new one.
        </li>
        <li>
          <strong>Add the server to your client config,</strong> pasting your token into the{' '}
          <code>Authorization</code> header (replace <code>&lt;YOUR_TOKEN&gt;</code>):
          <div className="log-viewer">
            <pre>{CONNECT_HTTP}</pre>
          </div>
          <span className="docs-note">
            Config location — <strong>Claude Desktop:</strong>{' '}
            <code>~/Library/Application Support/Claude/claude_desktop_config.json</code> ·{' '}
            <strong>Claude Code:</strong> your project's <code>.mcp.json</code> (or run{' '}
            <code>claude mcp add</code>).
          </span>
        </li>
        <li>
          <strong>Reconnect &amp; verify.</strong> Restart / reload your client — the{' '}
          <code>optisigns-infras</code> tools your role permits appear. Quick reachability check:
          <div className="log-viewer">
            <pre>{HEALTH_CHECK}</pre>
          </div>
        </li>
      </ol>
      <p className="docs-note">
        <strong>Entrypoint:</strong> <code>{ENTRYPOINT}</code> · <strong>Revoke</strong> a token any
        time in Settings → Access — its MCP access stops immediately. <strong>Local testing:</strong>{' '}
        run <code>pnpm --filter @infras/mcp dev</code> and point at{' '}
        <code>http://localhost:8080/mcp</code> — no token needed unless{' '}
        <code>MCP_REQUIRE_AUTH=true</code>.
      </p>
    </>
  );
}
